import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface PushHistorico {
  id_push: number;
  titulo: string;
  mensagem: string;
  publico_alvo: string;
  total_enviados: number;
  total_falhas: number;
  nome_admin: string;
  created_at: string;
}

const PushGeral: React.FC = () => {
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [publicoAlvo, setPublicoAlvo] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState<PushHistorico[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [resultado, setResultado] = useState<{ total_enviados: number; total_falhas: number } | null>(null);

  useEffect(() => {
    fetchHistorico();
  }, []);

  const fetchHistorico = async () => {
    setLoadingHistorico(true);
    try {
      const response = await api.get('/admin/push-geral/historico');
      setHistorico(response.data);
    } catch (error) {
      console.error('Erro ao buscar historico:', error);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleEnviar = async () => {
    if (!titulo.trim() || !mensagem.trim()) {
      alert('Preencha o titulo e a mensagem.');
      return;
    }

    const publicoLabel = publicoAlvo === 'todos' ? 'TODOS os usuarios' : `usuarios do tipo "${publicoAlvo}"`;
    const confirmado = window.confirm(
      `Tem certeza que deseja enviar esta notificacao para ${publicoLabel}?\n\nTitulo: ${titulo}\nMensagem: ${mensagem}`
    );

    if (!confirmado) return;

    setLoading(true);
    setResultado(null);

    try {
      const response = await api.post('/admin/push-geral', {
        titulo,
        mensagem,
        publico_alvo: publicoAlvo,
      });

      setResultado(response.data);
      setTitulo('');
      setMensagem('');
      setPublicoAlvo('todos');
      fetchHistorico();
    } catch (error: any) {
      alert('Erro ao enviar push: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  const getPublicoLabel = (alvo: string) => {
    const labels: Record<string, string> = {
      todos: 'Todos',
      usuario: 'Usuarios',
      banda: 'Bandas',
      empresa: 'Empresas',
    };
    return labels[alvo] || alvo;
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Enviar Push Geral</h1>

      <div style={styles.formSection}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Titulo da Notificacao</label>
          <input
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value.slice(0, 100))}
            placeholder="Ex: Nova atualizacao disponivel!"
            style={styles.input}
            maxLength={100}
          />
          <span style={styles.charCount}>{titulo.length}/100</span>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Mensagem</label>
          <textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value.slice(0, 500))}
            placeholder="Digite a mensagem da notificacao..."
            style={styles.textarea}
            maxLength={500}
            rows={4}
          />
          <span style={styles.charCount}>{mensagem.length}/500</span>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Publico-alvo</label>
          <select
            value={publicoAlvo}
            onChange={(e) => setPublicoAlvo(e.target.value)}
            style={styles.select}
          >
            <option value="todos">Todos</option>
            <option value="usuario">Usuarios</option>
            <option value="banda">Bandas</option>
            <option value="empresa">Empresas</option>
          </select>
        </div>

        <button
          onClick={handleEnviar}
          disabled={loading || !titulo.trim() || !mensagem.trim()}
          style={{
            ...styles.button,
            opacity: loading || !titulo.trim() || !mensagem.trim() ? 0.6 : 1,
          }}
        >
          {loading ? 'Enviando...' : 'Enviar Push'}
        </button>

        {resultado && (
          <div style={styles.resultado}>
            <p style={styles.resultadoTexto}>
              Enviados: <strong>{resultado.total_enviados}</strong> | Falhas: <strong>{resultado.total_falhas}</strong>
            </p>
          </div>
        )}
      </div>

      <div style={styles.historicoSection}>
        <h2 style={styles.subtitle}>Historico de Envios</h2>

        {loadingHistorico ? (
          <p style={styles.loadingText}>Carregando historico...</p>
        ) : historico.length === 0 ? (
          <p style={styles.emptyText}>Nenhum push enviado ainda.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Titulo</th>
                <th style={styles.th}>Publico</th>
                <th style={styles.th}>Enviados</th>
                <th style={styles.th}>Falhas</th>
                <th style={styles.th}>Admin</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((item) => (
                <tr key={item.id_push} style={styles.tr}>
                  <td style={styles.td}>{formatDate(item.created_at)}</td>
                  <td style={styles.td}>{item.titulo}</td>
                  <td style={styles.td}>{getPublicoLabel(item.publico_alvo)}</td>
                  <td style={styles.td}>{item.total_enviados}</td>
                  <td style={styles.td}>{item.total_falhas}</td>
                  <td style={styles.td}>{item.nome_admin || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto',
    color: '#fff',
  },
  title: {
    fontSize: '24px',
    color: '#ff6600',
    marginBottom: '30px',
    textAlign: 'center',
  },
  formSection: {
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '30px',
  },
  formGroup: {
    marginBottom: '20px',
    position: 'relative' as const,
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#ccc',
    fontWeight: 'bold',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#121212',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#121212',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#121212',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  charCount: {
    position: 'absolute' as const,
    right: '8px',
    bottom: '-18px',
    fontSize: '12px',
    color: '#666',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#ff6600',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '10px',
  },
  resultado: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#1e3a1e',
    borderRadius: '6px',
    textAlign: 'center' as const,
  },
  resultadoTexto: {
    margin: 0,
    color: '#4caf50',
    fontSize: '14px',
  },
  historicoSection: {
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    padding: '24px',
  },
  subtitle: {
    fontSize: '20px',
    color: '#ff6600',
    marginBottom: '20px',
  },
  loadingText: {
    color: '#999',
    textAlign: 'center' as const,
  },
  emptyText: {
    color: '#666',
    textAlign: 'center' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderBottom: '2px solid #333',
    color: '#ff6600',
    fontSize: '13px',
  },
  tr: {
    borderBottom: '1px solid #222',
  },
  td: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#ccc',
  },
};

export default PushGeral;
